import { useCallback } from 'react';
import type { Item, PlaybackState } from '../lib/types';
import { YouTubePlayer } from './YouTubePlayer';
import { TwitchPlayer } from './TwitchPlayer';

interface Props {
  item: Item;
  /** 上部バーの選択と対応させる通し番号 (1 始まり)。 */
  index: number;
  hasAudio: boolean;
  hidden: boolean;
  onToggleHidden: () => void;
  onRemove: () => void;
  onPlaybackState: (state: PlaybackState) => void;
}

/**
 * 1 配信ぶんのタイル。
 *
 * 表示は映像だけ。再生/停止・音量は各プレイヤーの純正 UI にあるので置かない。
 *
 * 「映像を隠す」は **unmount も display:none も使わない**。どちらもプレイヤーが
 * 破棄されるか再生が止まるため、タイルの高さを潰して中身をクリップするだけにする。
 * iframe は元の寸法のまま描画され続けるので音は鳴り続ける。
 */
export function PlayerCard({
  item,
  index,
  hasAudio,
  hidden,
  onToggleHidden,
  onRemove,
  onPlaybackState,
}: Props) {
  const onState = useCallback((s: PlaybackState) => onPlaybackState(s), [onPlaybackState]);
  const label = item.title;

  return (
    <section
      className="tile"
      data-provider={item.source.provider}
      data-audio={hasAudio ? 'on' : 'off'}
      data-hidden={hidden ? 'on' : 'off'}
      title={label}
    >
      {item.source.provider === 'youtube' ? (
        <YouTubePlayer source={item.source} muted={item.muted} onState={onState} />
      ) : (
        <TwitchPlayer source={item.source} muted={item.muted} onState={onState} />
      )}

      {hidden && (
        // 畳んだ状態の見出し。クリックで戻せるので、上部バーを隠していても復帰できる。
        <button className="tile-collapsed" onClick={onToggleHidden} title="映像を表示する">
          <span className="tile-index">{index}</span>
          {hasAudio && <span aria-label="音声オン">🔊</span>}
          <span className="tile-collapsed-label">{label}</span>
        </button>
      )}

      {!hidden && (
        <div className="tile-tag">
          <span className="tile-index">{index}</span>
          {hasAudio && <span aria-label="音声オン">🔊</span>}
        </div>
      )}

      <button className="tile-close" onClick={onRemove} aria-label="削除">
        ✕
      </button>
    </section>
  );
}
